<template>
  <main class="modal-wrapper bulk-edit">
    <svg-icon
      class="close-modal cursor"
      name="close"
      size="small"
      @click.native="closeModal()"
    />

    <Panel panel-class-name="modal-information">
      <sectionHeader
        slot="header"
        :title="isDelete ? 'Delete your transfers' : 'Edit your transfers'"
        :counter="bulkList.length"
      />

      <div class="modal-information-content" slot="content">
        <div class="bulk-edit-container">
          <div
            class="bulk-edit-item"
            v-for="transfer in bulkList"
            :key="transfer.id"
          >
            <div class="l-flex-row">
              <div
                class="row-item-status"
                title="Status"
                :class="transfer.status === 'inactive' ? 'item-inactive' : 'item-active'"
              />
              <div class="bulk-audience-id">
                {{ transfer.id }}
              </div>
            </div>

            <div class="bulk-audience-label">
              {{ transfer.label }}
            </div>
            <div class="bulk-audience-bootstrap">
              {{ transfer.bootstrap_status }}
            </div>
          </div>
        </div>
      </div>
    </Panel>

    <main class="modal-main modal__medium bulk-edit-main is-relative">
      <spinner
        class="is-pending"
        v-if="isUpdatedOrDeletedLoading"
        width="50"
        height="50"
      />

      <header class="modal-header">
        <sectionHeader :title="isDelete ? 'Delete transfers' : 'Edit transfers'" />
      </header>

      <!-- Edit form -->
      <div class="modal-body modal-content-audience" v-if="!isDelete">
        <div class="form-subsection">
          <el-select class="capitalize-label" v-model="editedField">
            <el-option
              v-for="type in editableFields"
              :key="type.value"
              :label="type.label"
              :value="type.value"
            />
          </el-select>
        </div>

        <!-- End Date -->
        <div class="l-flex-row has-gutter_medium end-date__block" v-if="editedField === 'endDate'">
          <div class="form-subsection">
            <div class="form-label__required">
              <label>End Date</label>
            </div>
            <el-date-picker
              v-model="endDate"
              name="end_date"
              :editable="true"
              :clearable="false"
              class="datepicker"
              type="date"
              :picker-options="endDateOptions"
              v-validate="'date_format:YYYY-MM-DD|required'"
              placeholder="Transfer end date"
            />
          </div>
        </div>

        <div class="form-section" v-else-if="editedField === 'bootstrap_status'">
          <spinner
            class="l-flex-row--center"
            v-if="bootstrapRetrieveIsLoading"
            width="50"
            height="50"
          />

          <div
            class="form-section--switch l-flex-row--between"
            v-else
            :disabled="noBootstrapableTransfer || bootstrapRetrieveIsLoading || dailyLimitBootstrapExceeded"
          >
            <div class="form-label l-flex-row flex-1">
              <label class="flex-1" for="switch-bootstrap">Push of stock</label>
            </div>

            <div class="onoffswitch">
              <input
                id="switch-bootstrap"
                name="switch-bootstrap"
                class="onoffswitch-checkbox"
                type="checkbox"
                v-model="bootstrapStatus"
                true-value="attribute"
                false-value="none"
              >
              <label class="onoffswitch-label" for="switch-bootstrap" />
            </div>
          </div>
          <div
            class="message-info form-validate-error l-flex-row--center"
            style="display: block"
            v-if="Object.keys(listTransferNoVolume).length > 0 && bootstrapStatus === 'none'"
          >
            <div class="l-flex-row--center">
              <svg-icon class="no-border" name="triangleExclamation" size="big" />
              <div class="message">
                The push of stock operation was only partially completed. We could not launch a push of stock operation on the following transfers:
              </div>
            </div>
            <ul>
              <li class="message" style="margin-left: 20px;" v-for="(message, id) in listTransferNoVolume" :key="id">
                {{ id + ' - ' + message }}
              </li>
            </ul>
          </div>
          <template v-else>
            <div
              class="message-info form-validate-error l-flex-row--center"
              style="display: block"
              v-if="noBootstrapableTransfer || (unBootstrapableList.length > 0 && bootstrapStatus === 'attribute')"
            >
              <div class="l-flex-row--center">
                <svg-icon class="no-border" name="triangleExclamation" size="big" />
                <div class="message">
                  We cannot launch a push of stock operation on the following transfers.
                </div>
              </div>
              <ul>
                <li class="message" v-for="item in unBootstrapableList" :key="item.id">
                  {{ item.id + ' - ' + item.label }}
                </li>
              </ul>
            </div>
            <div class="message-info form-validate-error l-flex-row--center" v-else-if="dailyLimitBootstrapExceeded">
              <div class="message">
                The daily push of stock quota has been reached or would be reached with the current selection
              </div>
            </div>

            <div class="message-info l-flex-row--center" v-else-if="bootstrapStatus === 'attribute'">
              <svg-icon class="no-border" name="info" size="medium" />
              <div class="message">
                Selected transfers will be pushed to stock
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Bulk delete -->
      <div class="modal-body modal-content-audience" v-else>
        <div class="message-info form-validate-error l-flex-row--center">
          <div class="l-flex-row--center">
            <svg-icon class="no-border" name="triangleExclamation" size="big" />
            <div class="message" style="font-size:12px">
              {{ `You are about to permanently delete ${bulkList.length} transfers. Do you want to proceed?` }}
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="modal-footer">
        <div class="footer-content l-flex-row--between">
          <button class="btn btn--medium btn--round" @click="closeModal">
            Cancel
          </button>
          <button
            class="btn btn--medium btn--round btn-rev btn-save-audience"
            @click="isDelete ? handleBulkDelete() : handleBulkEdit()"
            type="button"
            :class="{ 'ui-disabled': disabledButton || isUpdatedOrDeletedLoading }"
          >
            {{ isDelete ? 'Delete' :'Edit' }}
          </button>
        </div>
      </footer>
    </main>
  </main>
</template>


<script>
import SectionHeader from '@/components/common/SectionHeader'
import dayjs from 'dayjs'
import Panel from '@/components/common/Panel'
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import {  difference, isNil } from 'lodash'

export default {
    name: 'BulkEditFormTransfers',
    components: {
        SectionHeader,
        Panel
    },

    props: {
        bulkList: {
            type: Array,
            required: true
        },
        isDelete: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    mounted() {
        const editableFields = [
            {
                label: 'End Date',
                value: 'endDate',
            }
        ]
        
        if (this.displayPushOfStock) {
            editableFields.push({
                label: 'Push of stock',
                value: 'bootstrap_status',
            })
        }
        this.editableFields = editableFields
    },

    data() {
        return {
            editedField: {
                label: 'end date',
                value: 'endDate'
            },
            editableFields: [],
            endDate: dayjs().add(30, 'day').toDate(),
            bootstrapStatus: 'none',
            bootstrapRetrieveIsLoading: false,
            noBootstrapableTransfer: false,
            dailyLimitBootstrapExceeded: false,
            bulkListForBootstrap: [],
            unBootstrapableList: []
        }
    },

    computed: {
        ...mapState('data/settings', {
            bootstrapPush: state => state.general.bootstrapPush
        }),
        ...mapState('data/transfer', {
            isUpdatedOrDeletedLoading: state => state.isUpdatedOrDeletedLoading,
            listTransferNoVolume: state => state.listTransferNoVolume,
            updatedBulkList: state => state.updatedBulkList
        }),
        ...mapState('account', {
            userRole: state => state.userRole,
            dailyPushOfStockLimit: state => state.dailyPushOfStockLimit,
            attributionCounterTransfer: state => state.attributionCounterTransfer,

        }),
        ...mapGetters('account', ['getUserAccess', 'getIsMonitoringActionAllowed']),

        monitoringActionAllowed() {
            return this.getIsMonitoringActionAllowed()
        },
        disabledButton() {
            if (this.isDelete) return false

            if (this.editedField === 'endDate') {
                const date = dayjs().startOf('day')
                return dayjs(this.endDate).isBefore(date)
            } else if (this.editedField === 'bootstrap_status') {
                return this.bootstrapStatus === 'none' || this.dailyLimitBootstrapExceeded
            }
            return true
        },

        endDateOptions() {
            return {
                firstDayOfWeek: 1,
                disabledDate(time) {
                    return (
                        dayjs()
                            .startOf('day')
                            .valueOf() >
                            dayjs(time)
                                .startOf('day')
                                .valueOf() ||
                        dayjs(time).valueOf() >=
                            dayjs()
                                .startOf('day')
                                .add(395, 'day')
                                .valueOf()
                    )
                }
            }
        },

        unBootstrapableMessage() {
            const unBootstrapableList = this.unBootstrapableList.map(el => {`${el.id} - ${el.label} `})
            return `The following transfers cannot be pushed to stock. They may already have been pushed to stock${this.bootstrapTextForRegular}: \n ${unBootstrapableList.join(
                '<br>'
            )}`
        },
        bootstrapTextForRegular () {
            return this.userRole === 'webo_user' ? ' or contain 3rd party formula' : ''
        },
        displayPushOfStock() {
            if (this.isWeboAdmin) return true
            if (this.isWeboEmployee && this.bootstrapPush) return true
            if (this.bootstrapPush && this.monitoringActionAllowed) return true
            return false
        },
    },

    watch: {
        editedField: {
            handler: function(val) {
                if (val === 'bootstrap_status') this.launchBootstrapRequests()
            }
        },

        endDate(val, oldVal) {
            if (val === null || val === '') {
                this.endDate = oldVal ?? dayjs()
            }
        },
        bulkList: {
            handler() {
                this.launchBootstrapRequests(false)
            },
        }
    },

    methods: {
        ...mapMutations('ui/transfers', ['setDisplayModal']),
        ...mapMutations('data/transfer', ['setListTransferNoVolume', 'setUpdatedBulkList', 'setResetBulkSelectedRows']),
        ...mapActions('data/transfer', ['bulkEditTransfers', 'bulkDeleteTransfers']),
        ...mapActions('dashboardAccount', ['retrieveExpiringAudiences', 'retrieveExpiringTransfers']),
        ...mapActions('account', ['retrieveAttributionCounterTransfer']),

        closeModal() {
            this.setDisplayModal({ item: 'bulkEdit', display: false })
            this.setListTransferNoVolume([])
            this.setUpdatedBulkList([])
        },

        isTransferExpired(transfer) {
            return dayjs().isAfter(dayjs(transfer.endDate), 'day')
        },

        async handleBulkEdit() {
            const list = this.editedField === 'bootstrap_status' ? this.bulkListForBootstrap : this.bulkList
            const payload = {
                'bootstrap_status': { list, type: 'push_of_stock' },
                'endDate': { list, end_date: dayjs(this.endDate).format('YYYY-MM-DD'), type: 'end_date' }
            }
            const response = await this.bulkEditTransfers(payload[this.editedField])
            this.retrieveExpiringAudiences()
            this.retrieveExpiringTransfers()
            if (Object.keys(this.listTransferNoVolume).length) {
                this.bootstrapStatus = 'none'
            }
            if (!isNil(response) && response?.status === 'OK') {
                this.$emit('bulkEditFinished')
                this.closeModal()
            }
        },

        async handleBulkDelete() {
            await this.bulkDeleteTransfers(this.bulkList)
            this.$nextTick(() => {
                this.retrieveExpiringAudiences()
                this.retrieveExpiringTransfers()
                this.closeModal()
                this.$emit('bulkEditFinished')
            })
        },

        async launchBootstrapRequests(isLoading = true) {
            // await this.retrieveAttributionCounterTransfer()
            this.bootstrapRetrieveIsLoading = isLoading
            const list = [...this.bulkList].filter(transfer => 
                transfer.bootstrap_status === 'not_applicable' &&
                !this.isTransferExpired(transfer) &&
                !['pixel url'].includes(transfer.partner.toLowerCase()) &&
                (
                  transfer.audienceInfo?.type === 'cclones'
                    || transfer.audienceInfo?.has_1st_party ||
                    this.userRole === 'webo_admin' ||
                    this.userRole === 'webo_employee'
                )
            )
            this.unBootstrapableList = difference(this.bulkList, list)
            if (list.length < 1) {
                this.bootstrapRetrieveIsLoading = false
                this.noBootstrapableTransfer = true
                return
            }

            if (this.userRole !== 'webo_admin' && this.attributionCounterTransfer > this.dailyPushOfStockLimit) {
                this.dailyLimitBootstrapExceeded = true
            }
            this.bulkListForBootstrap = list
            this.bootstrapRetrieveIsLoading = false
        },
    }
}
</script>

<style lang="scss">
.end-date__block {
    flex-direction: column !important;
    .form-subsection {
        min-height: auto;
        &:not(:first-child) {
            margin-left: 0;
            width: 100%;
            background: rgba(255, 103, 0, 0.1);
            border-radius: 4px;
            align-content: center;
            display: flex;
            margin-top: 10px;
            padding: 10px;
            .message-info {
                margin-top: 0;
                .message {
                    color: black;
                    margin-top: 0;
                }
            }
        }
    }
    svg {
        path {
            fill: rgba(255, 103, 0, 0.6);
        }
    }
}
</style>
